/* ============================================================
   Cloudflare Turnstile widget (bot/spam protection).
   Loads the CF script once, renders the widget, and reports the
   token via onVerify(token) — or onVerify(null) on expire/error so
   the caller can disable submit until a fresh token arrives.
   Site key is public; defaults to the landing's key, overridable via
   VITE_TURNSTILE_SITE_KEY.
   ============================================================ */
import React, { useEffect, useRef } from 'react';

const SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY || '0x4AAAAAADWWlxGfJdjyCQ7P';
const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

let scriptPromise = null;
function loadScript() {
  if (typeof window === 'undefined') return Promise.reject(new Error('no window'));
  if (window.turnstile) return Promise.resolve();
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = SCRIPT_SRC;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('turnstile script failed'));
    document.head.appendChild(s);
  });
  return scriptPromise;
}

export default function Turnstile({ onVerify, onExpire, theme = 'auto', resetSignal = 0 }) {
  const ref = useRef(null);
  const widgetId = useRef(null);
  const firstReset = useRef(true);

  // Re-run the challenge to mint a FRESH single-use token whenever the caller
  // bumps resetSignal (e.g. after a failed submit consumed the previous token —
  // without this the widget stays "solved" with a stale token and the submit
  // button never re-enables). Skips the initial mount.
  useEffect(() => {
    if (firstReset.current) { firstReset.current = false; return; }
    try {
      if (widgetId.current !== null && window.turnstile) {
        window.turnstile.reset(widgetId.current);
      }
    } catch (_) { /* noop */ }
  }, [resetSignal]);

  useEffect(() => {
    let cancelled = false;
    loadScript()
      .then(() => {
        const tryRender = () => {
          if (cancelled) return;
          if (!window.turnstile || !ref.current) { setTimeout(tryRender, 120); return; }
          if (widgetId.current !== null) return;
          try {
            widgetId.current = window.turnstile.render(ref.current, {
              sitekey: SITE_KEY,
              theme,
              callback: (token) => onVerify?.(token),
              'expired-callback': () => { onVerify?.(null); onExpire?.(); },
              'error-callback': () => { onVerify?.(null); },
            });
          } catch (_) { /* already rendered / transient */ }
        };
        tryRender();
      })
      .catch(() => { /* fail silently — submit stays disabled (no token) */ });

    return () => {
      cancelled = true;
      try {
        if (widgetId.current !== null && window.turnstile) window.turnstile.remove(widgetId.current);
      } catch (_) { /* noop */ }
      widgetId.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <div ref={ref} className="cf-turnstile" style={{ minHeight: 65 }} />;
}
